import { doc, setDoc, getDoc, collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from './firebase';

class TermsService {
  /**
   * Get the current/latest terms of service
   */
  async getCurrentTerms() {
    try {
      const termsRef = collection(db, 'termsOfService');
      const q = query(termsRef, orderBy('version', 'desc'), limit(1));
      const snapshot = await getDocs(q);
      
      if (!snapshot.empty) {
        const doc = snapshot.docs[0];
        return { id: doc.id, ...doc.data() };
      }
      
      return null;
    } catch (error) {
      console.error('Error getting current terms:', error);
      return null;
    }
  }

  /**
   * Get terms by version number
   */
  async getTermsByVersion(version) {
    try {
      const termsRef = collection(db, 'termsOfService');
      const q = query(termsRef, where('version', '==', version), limit(1));
      const snapshot = await getDocs(q);
      
      if (!snapshot.empty) {
        const doc = snapshot.docs[0];
        return { id: doc.id, ...doc.data() };
      }
      
      return null;
    } catch (error) {
      console.error('Error getting terms by version:', error);
      return null;
    }
  }

  /**
   * Record user acceptance of terms
   */
  async recordAcceptance(userId, termsVersion) {
    try {
      const acceptanceRef = doc(db, 'termsAcceptances', userId);
      
      await setDoc(acceptanceRef, {
        userId,
        currentVersion: termsVersion,
        acceptanceHistory: [
          {
            version: termsVersion,
            acceptedAt: new Date().toISOString(),
            userAgent: navigator.userAgent,
            ipAddress: null // You can add IP tracking if needed
          }
        ],
        lastUpdated: new Date().toISOString()
      }, { merge: true });

      return { success: true };
    } catch (error) {
      console.error('Error recording terms acceptance:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Check if user has accepted the current terms
   */
  async hasAcceptedCurrentTerms(userId) {
    try {
      const currentTerms = await this.getCurrentTerms();
      if (!currentTerms) return true; // No terms published yet
      
      const acceptanceRef = doc(db, 'termsAcceptances', userId);
      const acceptanceDoc = await getDoc(acceptanceRef);
      
      if (!acceptanceDoc.exists()) return false;
      
      const acceptance = acceptanceDoc.data();
      return acceptance.currentVersion === currentTerms.version;
    } catch (error) {
      console.error('Error checking terms acceptance:', error);
      return false;
    }
  }

  /**
   * Get user's terms acceptance record
   */
  async getUserAcceptance(userId) {
    try {
      const acceptanceRef = doc(db, 'termsAcceptances', userId);
      const acceptanceDoc = await getDoc(acceptanceRef);
      
      if (acceptanceDoc.exists()) {
        return acceptanceDoc.data();
      }
      
      return null;
    } catch (error) {
      console.error('Error getting user acceptance:', error);
      return null;
    }
  }

  /**
   * Create/update terms of service (admin only)
   * This would typically be called from an admin panel
   */
  async publishTerms(version, content, effectiveDate = new Date().toISOString()) {
    try {
      const termsId = `terms_v${version}`;
      const termsRef = doc(db, 'termsOfService', termsId);
      
      await setDoc(termsRef, {
        version,
        content,
        effectiveDate,
        publishedAt: new Date().toISOString(),
        status: 'active'
      });

      return { success: true, termsId };
    } catch (error) {
      console.error('Error publishing terms:', error);
      return { success: false, error: error.message };
    }
  }
}

export default new TermsService();